import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { getQrCode, getQrCodeAudit } from '../api/qrCodes';
import type { QrCode, QrCodeAuditEntry } from '../api/types';
import { AuditChange, auditChangeHeight } from '../components/AuditChange';
import { DataGrid } from '../components/DataGrid';
import { ErrorBanner } from '../components/ErrorBanner';
import { COMPACT_SCREEN, MEDIUM_SCREEN, useMediaQuery } from '../hooks/useMediaQuery';
import { formatTimestamp } from '../format';

/** Ten to a page, like the dashboard: a code repointed weekly for a year is fifty entries. */
const PAGE_SIZE = 10;

/**
 * A code's history: who changed what, when, and what it became. Read-only, and not by convention - the API
 * offers no verb but GET on this route, and the database refuses to change an entry once written.
 */
export function QrAuditPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const compact = useMediaQuery(COMPACT_SCREEN);
  const medium = useMediaQuery(MEDIUM_SCREEN);

  const [entries, setEntries] = useState<QrCodeAuditEntry[]>([]);
  const [qrCode, setQrCode] = useState<QrCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setLoading(true);
    getQrCodeAudit(id)
      .then(setEntries)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [id]);

  // Only for the heading: a super admin can read the history of a code that has been deleted, and then there
  // is no code left to read, so a failure here costs the title and nothing else.
  useEffect(() => {
    getQrCode(id)
      .then(setQrCode)
      .catch(() => setQrCode(null));
  }, [id]);

  const columns = useMemo<ColDef<QrCodeAuditEntry>[]>(() => {
    const all: ColDef<QrCodeAuditEntry>[] = [
      {
        field: 'occurredAt',
        headerName: t('qrCode.audit.when'),
        // To the second: two changes a minute apart are a correction, two in the same second are one save.
        valueFormatter: (cell) => formatTimestamp(cell.value as string),
        flex: 1.2,
        minWidth: 150,
        sort: 'desc',
      },
      {
        field: 'action',
        headerName: t('qrCode.audit.what'),
        valueFormatter: (cell) => t(`qrCode.audit.actions.${cell.value as string}`),
        flex: 1,
        minWidth: 120,
      },
      {
        colId: 'change',
        headerName: t('qrCode.audit.change'),
        sortable: false,
        cellRenderer: ({ data }: ICellRendererParams<QrCodeAuditEntry>) =>
          data && <AuditChange action={data.action} before={data.oldValue} after={data.newValue} />,
        flex: 2,
        minWidth: 200,
      },
      {
        colId: 'actor',
        headerName: t('qrCode.audit.who'),
        // The name where the directory knew one, then the address, then the object id: never blank, because
        // "who" is the question this page exists to answer.
        valueGetter: (cell) => cell.data?.actorName || cell.data?.actorEmail || cell.data?.actorUserId || '',
        flex: 1.2,
        minWidth: 160,
      },
    ];

    // Narrow screens keep the two that carry the answer; the rest arrive as there is room for them.
    const kept = compact ? ['occurredAt', 'action'] : medium ? ['occurredAt', 'action', 'change'] : null;

    return kept === null
      ? all
      : all.filter((column) => kept.includes(column.field ?? column.colId ?? ''));
  }, [compact, medium, t]);

  const name = qrCode?.title || entries[0]?.slug || '';

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Link to={`/qr/${id}`} className="btn btn-sm btn-outline-secondary">
          <i className="pi pi-arrow-left me-1" aria-hidden="true" />
          {t('qrCode.audit.backToCode')}
        </Link>
        <h2 className="h4 mb-0">{name ? t('qrCode.audit.titleFor', { name }) : t('qrCode.audit.title')}</h2>
      </div>

      <p className="text-muted small">{t('qrCode.audit.readOnly')}</p>

      <ErrorBanner error={error} />

      <DataGrid
        rows={entries}
        columns={columns}
        getRowId={(entry) => entry.id}
        label={t('qrCode.audit.title')}
        emptyMessage={t('qrCode.audit.empty')}
        loading={loading}
        pageSize={PAGE_SIZE}
        rowHeight={40}
        getRowHeight={(entry) => auditChangeHeight(entry.action, entry.oldValue, entry.newValue)}
      />
    </>
  );
}
